/*
  OSMODI conversion and UI fixes, draft theme 190583472421, 2026-08-11

  Additive only. This file changes no price, policy, identity, shipping,
  return, availability, variant association, or structured-data value. It only
  repairs interface state that the existing scripts leave stuck.

  The mobile sticky buy bar defect:

  Three scripts wrote the same data-visible attribute on the same element.
  osmodi-commerce.js drove it from a cached IntersectionObserver rectangle,
  osmodi-product-reference-v2.js drove it from a scroll handler, and the first
  version of this file added a third writer. Neither of the existing two can be
  safely disabled, so the attribute could not be held: a shopper on a phone
  never saw an add to bag control after scrolling past the first one.

  This file no longer competes. It drives its own data-osmodi-sticky attribute,
  which nothing else writes, and osmodi-conversion-fixes.css makes that
  attribute authoritative in both directions. The full reasoning, with the
  exact offending lines, is in section 2 of that stylesheet.

  This file contains no Unicode em dash characters.
*/

(() => {
  'use strict';

  function initStickyBuyBar() {
    const bar = document.querySelector('[data-oc-stickybuy]');
    const anchor = document.querySelector('[data-oc-buy-anchor]');
    if (!bar || !anchor) return;
    if (bar.dataset.osmodiFixedOwner === 'conversion-fixes') return;

    bar.dataset.osmodiFixedOwner = 'conversion-fixes';

    // Show the bar once the primary buy block has scrolled out of view above
    // the viewport. Never show it while the real control is on screen, so a
    // shopper is not offered two competing buttons.
    //
    // This writes data-osmodi-sticky, not data-visible. osmodi-commerce.js and
    // osmodi-product-reference-v2.js both write data-visible for this same
    // element and neither can be safely disabled, so competing for it is not
    // winnable. See the long note in osmodi-conversion-fixes.css section 2.
    // Every read here is a live getBoundingClientRect, never a cached
    // IntersectionObserver rectangle, which is the bug in commerce.js.
    const apply = () => {
      const next = anchor.getBoundingClientRect().bottom < 0 ? 'show' : 'hide';
      if (bar.dataset.osmodiSticky !== next) bar.dataset.osmodiSticky = next;
    };

    // The observer is only a change notifier. Its entry is deliberately
    // ignored and the measurement is taken fresh inside apply.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(apply, { threshold: [0, 1] }).observe(anchor);
    }

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        apply();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('orientationchange', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });

    apply();
  }

  // The sticky bar repeats the size choice. Keep its pressed state in step
  // with the main selector so the two never disagree about which variant is
  // selected. Visible selection, price, button label and cart line have to
  // describe the same variant.
  function syncStickySizeState() {
    const section = document.querySelector('.section-osmodi-oc-product');
    const input = section && section.querySelector('[data-oc-variant-input]');
    if (!section || !input) return;

    const apply = () => {
      const selectedId = input.value;
      if (!selectedId) return;
      section.querySelectorAll('.oc-stickybuy__size').forEach((choice) => {
        choice.setAttribute('aria-current', choice.dataset.variantId === selectedId ? 'true' : 'false');
      });
    };

    new MutationObserver(apply).observe(input, { attributes: true, attributeFilter: ['value'] });
    section.addEventListener('change', apply);
    section.addEventListener('click', () => window.setTimeout(apply, 0));
    apply();
  }

  function init() {
    initStickyBuyBar();
    syncStickySizeState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', init);
  document.addEventListener('shopify:section:load', init);
})();
